import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useWindow } from '@krp/client-runtime'
import {
  PLATFORM_WINDOWS,
  ResourceEditorScreen,
  makeSelectResourceEditorVm,
  platformCreateResource,
  platformResources,
  platformResource,
  platformUpdateResource,
  discardSecret,
  stashSecret,
  type ConnectionFormContext,
  type ConnectionFormExtension,
  type PlatformResourceArgs,
  type ResourceEditorActions,
} from '@krp/platform-ui'

import { useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { CONNECTION_FORMS } from '../../composition/connectionForms'
import { useConnectors } from './useConnectors'
import { useMyAccess } from './useMyAccess'

const selectResources = platformResources.select()
const NO_CHOICES: ConnectionFormContext['choices'] = {}

/**
 * Читает варианты выбора хуком формы вида и отдаёт их родителю: форма без `useChoices` хук не вызывает, а порядок
 * хуков внутри одного компонента остаётся постоянным.
 */
function ChoicesProbe({
  form,
  resourceId,
  onChoices,
}: {
  form: ConnectionFormExtension
  resourceId: string | null
  onChoices: (choices: ConnectionFormContext['choices']) => void
}) {
  const choices = form.useChoices?.(resourceId) ?? NO_CHOICES
  useEffect(() => onChoices(choices), [choices, onChoices])
  return null
}

/**
 * Binding формы ресурса — оба окна: «Новый источник» (вид из адреса) и «Изменить
 * ресурс» (rid из адреса). Секрет из формы уходит в команду через
 * `stashSecret`: в аргументах мутации (они живут в Redux) — только ссылка.
 */
export function ResourceEditorBinding() {
  const window = useWindow()
  const mode = window.kind === PLATFORM_WINDOWS.resourceEdit ? 'edit' : 'create'
  const resourceId = window.params.rid ?? ''
  const args = useMemo<PlatformResourceArgs>(() => ({ resourceId }), [resourceId])
  const { refetch } = platformResource.useQuerySubscription(args, { skip: mode !== 'edit' || resourceId === '' })
  // «Перечитать» начинает форму заново с данных сервера: черновик пересоздаётся новым ключом.
  const [generation, setGeneration] = useState(0)
  const access = useMyAccess()
  const connectors = useConnectors()
  const [create, createState] = platformCreateResource.useMutation()
  const [update, updateState] = platformUpdateResource.useMutation()
  const resetUpdate = updateState.reset

  const selectQuery = useMemo(() => platformResource.select(args), [args])
  const query = useAppSelector((state: RootState) => selectQuery(state))
  const kind = mode === 'edit' ? (query.data?.kind ?? '') : (window.params.kind ?? '')
  const form = CONNECTION_FORMS.find(kind)
  // Реестр читает тот, у кого есть право на него; без права форма видит пустой список и просит ввести значение руками.
  platformResources.useQuerySubscription(undefined, { skip: form === undefined })
  const registry = useAppSelector((state: RootState) => selectResources(state))
  const [choices, setChoices] = useState<ConnectionFormContext['choices']>(NO_CHOICES)
  const onChoices = useCallback((next: ConnectionFormContext['choices']) => setChoices(next), [])
  const formContext = useMemo<ConnectionFormContext>(
    () => ({
      resourceId: mode === 'edit' ? resourceId : null,
      resources: (registry.data ?? []).map(({ id, kind, displayName, enabled }) => ({ id, kind, displayName, enabled })),
      choices,
    }),
    [choices, mode, registry.data, resourceId],
  )
  const selectVm = useMemo(() => makeSelectResourceEditorVm(), [])
  const vm = selectVm({
    mode,
    kind,
    ...(mode === 'edit' ? { query } : {}),
    access,
    forms: CONNECTION_FORMS,
    connectors: connectors.query,
    save: mode === 'edit' ? updateState : createState,
  })

  // Ответ правки может прийти после закрытия окна — тогда закрывать уже нечего.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  // Версия договора — из манифеста вида с сервера: форма пакета вида её не держит.
  const contractVersion = connectors.registry.find(kind)?.contractVersion ?? ''
  const refetchConnectors = connectors.refetch
  const actions = useMemo<ResourceEditorActions>(
    () => ({
      submit: ({ draft, secret }) => {
        const secretRef = stashSecret(secret)
        if (mode === 'edit') {
          void update({ resourceId, draft, secretRef })
            .unwrap()
            .then(() => {
              if (alive.current) window.close()
            })
            .catch(() => undefined)
            .finally(() => discardSecret(secretRef))
          return
        }
        // Созданный ресурс показывается в форме: карточку сервер откроет, лишь когда rid попадёт в сессию.
        void create({ kind, contractVersion, draft, secretRef }).finally(() => discardSecret(secretRef))
      },
      cancel: window.close,
      reload: () => {
        resetUpdate()
        void refetchConnectors()
        if (mode === 'edit') void refetch()
        setGeneration((value) => value + 1)
      },
      openCreated: () => {
        if (createState.data !== undefined) window.replaceWith(PLATFORM_WINDOWS.resource, { rid: createState.data.id })
      },
      // Форма нового источника открыта из плиток: назад — к ним, на то же место в стопке окон; введённое не хранится.
      ...(mode === 'create' ? { backToKinds: () => window.replaceWith(PLATFORM_WINDOWS.sourceAdd) } : {}),
    }),
    [contractVersion, create, createState.data, kind, mode, refetch, refetchConnectors, resetUpdate, resourceId, update, window],
  )

  return (
    <>
      {form?.useChoices !== undefined && (
        <ChoicesProbe key={kind} form={form} resourceId={mode === 'edit' ? resourceId : null} onChoices={onChoices} />
      )}
      <ResourceEditorScreen
        key={`${mode}:${resourceId}:${kind}:${generation}`}
        vm={vm}
        actions={actions}
        form={form}
        context={formContext}
      />
    </>
  )
}
